// Editor de source do template (§4.5) — CodeMirror (HTML p/ EMAIL,
// JSON p/ WHATSAPP) com API imperativa de inserção no cursor
// (usada pelos chips do painel de variáveis).
import { history } from "@codemirror/commands"
import { html } from "@codemirror/lang-html"
import { json } from "@codemirror/lang-json"
import { oneDark } from "@codemirror/theme-one-dark"
import { EditorView } from "@codemirror/view"
import type { ReactCodeMirrorRef } from "@uiw/react-codemirror"
import CodeMirror from "@uiw/react-codemirror"
import {
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react"

export type TemplateSourceEditorHandle = {
  // Insere o texto na posição atual do cursor (fallback: fim).
  insertAtCursor: (text: string) => void
}

type TemplateSourceEditorProps = {
  value: string
  onChange: (value: string) => void
  language: "html" | "json"
  "aria-label"?: string
}

export const TemplateSourceEditor = forwardRef<
  TemplateSourceEditorHandle,
  TemplateSourceEditorProps
>(function TemplateSourceEditor(
  { value, onChange, language, ...ariaProps },
  ref
) {
  const editorRef = useRef<ReactCodeMirrorRef>(null)

  useImperativeHandle(ref, () => ({
    insertAtCursor: (text: string) => {
      const view = editorRef.current?.view
      if (!view) {
        onChange(value + text)
        return
      }
      const { from, to } = view.state.selection.main
      view.dispatch({
        changes: { from, to, insert: text },
        selection: { anchor: from + text.length },
      })
      view.focus()
    },
  }))

  return (
    <CodeMirror
      ref={editorRef}
      value={value}
      onChange={onChange}
      extensions={[
        language === "html" ? html() : json(),
        EditorView.lineWrapping,
        history(),
      ]}
      theme={oneDark}
      className="rounded-lg text-sm"
      height="560px"
      maxWidth="100%"
      indentWithTab={true}
      basicSetup={{
        tabSize: 2,
        lineNumbers: true,
        highlightActiveLine: true,
        history: true,
        foldGutter: language === "json",
        bracketMatching: true,
        closeBrackets: true,
        autocompletion: true,
      }}
      {...ariaProps}
    />
  )
})
